import { Fragment, memo } from 'react';

import TypographyCompo from '../../../components/Comman/TypographyCompo';
import { SECTION_IDS } from '../constants';
import {
  CHALLAN_LEGEND_COLORS,
  DASHBOARD_COLORS,
  FUNNEL_COLOR_MAP,
  SECTION_ICON_BACKGROUNDS,
} from '../styles/colors';
import {
  ChartSkeleton,
  DonutAnalysisSkeleton,
  FunnelSkeleton,
  isBlockLoaded,
  KpiGridSkeleton,
  ProgressAnalysisSkeleton,
  SequentialBlock,
  TableSkeleton,
} from '../components/loading';
import {
  CardShell,
  KpiGrid,
  ProgressBar,
  SectionHeader,
} from '../components/DashboardUI';
import { SearchableDashboardTable } from '../components/SearchableDashboardTable';
import {
  DRIVER_UTILIZATION_COLUMNS,
  PENDING_SETTLEMENTS_COLUMNS,
  PENDING_TRANSFERS_COLUMNS,
  RECENT_CHALLANS_COLUMNS,
} from '../config/tableColumns';

const CHALLAN_LEGEND_ITEMS = [
  { key: 'delivered', label: 'Delivered' },
  { key: 'dispatched', label: 'Active Challans' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancel' },
];

const LegendItem = memo(function LegendItem({ dotColor, label, value }) {
  return (
    <div className="legend-item">
      <div className="legend-dot" style={{ background: dotColor }} />
      <TypographyCompo color={DASHBOARD_COLORS.text2} component="span">
        {label} {value}
      </TypographyCompo>
    </div>
  );
});

const DonutCenter = memo(function DonutCenter({ total }) {
  return (
    <div className="donut-center">
      <TypographyCompo
        className="donut-center-val"
        color={DASHBOARD_COLORS.blue}
        component="div"
      >
        {total}
      </TypographyCompo>
      <TypographyCompo
        className="donut-center-lbl"
        color={DASHBOARD_COLORS.text2}
        component="div"
      >
        Total
      </TypographyCompo>
    </div>
  );
});

const FunnelStep = memo(function FunnelStep({ step }) {
  const colors = FUNNEL_COLOR_MAP[step.color];

  return (
    <div
      className="funnel-step"
      style={{
        background: colors.bg,
        borderColor: colors.border,
        borderWidth: '.5px',
      }}
    >
      <TypographyCompo className="funnel-val" color={colors.text} component="div">
        {step.value}
      </TypographyCompo>
      <TypographyCompo className="funnel-lbl" color={DASHBOARD_COLORS.text2} component="div">
        {step.label}
      </TypographyCompo>
    </div>
  );
});

export function SummarySection({ summaryKpis, loadingState }) {
  return (
    <section className="dash-section" id={SECTION_IDS.SUMMARY}>
      <SectionHeader
        icon="📊"
        iconBackground={SECTION_ICON_BACKGROUNDS.blue}
        title="Dashboard Summary"
        count={
          isBlockLoaded(loadingState, 'summary', 'kpis')
            ? `${summaryKpis.length} metrics`
            : null
        }
      />
      <SequentialBlock
        loadingState={loadingState}
        section="summary"
        block="kpis"
        skeleton={<KpiGridSkeleton columns="g10" count={10} />}
      >
        <KpiGrid items={summaryKpis} />
      </SequentialBlock>
    </section>
  );
}

export function ChallanSection({ challanStatus, recentChallans, loadingState }) {
  return (
    <section className="dash-section" id={SECTION_IDS.CHALLAN}>
      <SectionHeader
        icon="📄"
        iconBackground={SECTION_ICON_BACKGROUNDS.blue}
        title="Challan Management"
      />

      <div className="section-body">
        <div className="g2">
          <CardShell title="Challan Status">
            <SequentialBlock
              loadingState={loadingState}
              section="challan"
              block="status"
              skeleton={<DonutAnalysisSkeleton />}
            >
              <div className="card-body">
                <div className="donut-wrap">
                  <div className="donut-canvas-wrap" style={{ width: 110, height: 110 }}>
                    <canvas id="challanDonut" width="110" height="110" />
                    <DonutCenter total={challanStatus.total} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="legend">
                      {CHALLAN_LEGEND_ITEMS.map((item, index) => (
                        <LegendItem
                          key={item.key}
                          dotColor={CHALLAN_LEGEND_COLORS[index]}
                          label={item.label}
                          value={challanStatus[item.key]}
                        />
                      ))}
                    </div>
                    {challanStatus.progressBars.map((bar) => (
                      <ProgressBar
                        key={bar.label}
                        label={bar.label}
                        value={bar.value}
                        width={bar.width}
                        color={bar.color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </SequentialBlock>
          </CardShell>

          <CardShell title="7-Day Challan Trend">
            <SequentialBlock
              loadingState={loadingState}
              section="challan"
              block="trend"
              skeleton={<ChartSkeleton height={190} />}
            >
              <div className="card-body">
                <div className="chart-box" style={{ height: 190 }}>
                  <canvas id="challanTrend" />
                </div>
              </div>
            </SequentialBlock>
          </CardShell>
        </div>

        <CardShell title="Recent Challans">
          <SequentialBlock
            loadingState={loadingState}
            section="challan"
            block="table"
            skeleton={<TableSkeleton rows={5} columns={RECENT_CHALLANS_COLUMNS.length} />}
          >
            <SearchableDashboardTable
              columns={RECENT_CHALLANS_COLUMNS}
              rows={recentChallans}
              placeholder="Search challan no, driver, status..."
              getRowKey={(row) => row.id || `${row.challanNo}-${row.index}`}
            />
          </SequentialBlock>
        </CardShell>
      </div>
    </section>
  );
}

export function DriverSection({
  driverKpis,
  driverUtilization,
  driverProgress,
  loadingState,
}) {
  return (
    <section className="dash-section" id={SECTION_IDS.DRIVER}>
      <SectionHeader
        icon="🚗"
        iconBackground={SECTION_ICON_BACKGROUNDS.teal}
        title="Driver & Vehicle Monitoring"
      />

      <div className="section-body">
        <SequentialBlock
          loadingState={loadingState}
          section="driver"
          block="kpis"
          skeleton={<KpiGridSkeleton columns="g5" count={5} />}
        >
          <KpiGrid items={driverKpis} columns="g5" />
        </SequentialBlock>

        <div className="g2">
          <CardShell title="Driver Utilization">
            <SequentialBlock
              loadingState={loadingState}
              section="driver"
              block="utilization"
              skeleton={
                <TableSkeleton
                  rows={5}
                  columns={DRIVER_UTILIZATION_COLUMNS.length}
                />
              }
            >
              <SearchableDashboardTable
                columns={DRIVER_UTILIZATION_COLUMNS}
                rows={driverUtilization}
                scrollable
                placeholder="Search driver name, status..."
                getRowKey={(row) => row.id || row.name}
              />
            </SequentialBlock>
          </CardShell>

          <CardShell title="Delivery Progress per Driver">
            <SequentialBlock
              loadingState={loadingState}
              section="driver"
              block="progress"
              skeleton={<ProgressAnalysisSkeleton barCount={4} chartHeight={130} />}
            >
              <div className="card-body">
                {driverProgress.map((driver) => (
                  <ProgressBar
                    key={driver.id || driver.name}
                    label={driver.name}
                    value={`${driver.delivered}/${driver.assigned} · ${driver.percent}%`}
                    width={`${driver.percent}%`}
                    color={driver.color}
                  />
                ))}
                <div className="chart-box" style={{ height: 130, marginTop: 8 }}>
                  <canvas id="driverChart" />
                </div>
              </div>
            </SequentialBlock>
          </CardShell>
        </div>
      </div>
    </section>
  );
}

export function StockTransferSection({ stockTransferFunnel, pendingTransfers, loadingState }) {
  return (
    <section className="dash-section" id={SECTION_IDS.STOCK_TRANSFER}>
      <SectionHeader
        icon="🔄"
        iconBackground={SECTION_ICON_BACKGROUNDS.violet}
        title="Stock Transfer Dashboard"
      />

      <div className="section-body">
        <div className="card">
          <SequentialBlock
            loadingState={loadingState}
            section="stockTransfer"
            block="funnel"
            skeleton={<FunnelSkeleton stepCount={4} />}
          >
            <div className="funnel">
              {stockTransferFunnel.map((step, index) => (
                <Fragment key={step.key}>
                  {index > 0 ? (
                    <TypographyCompo
                      className="funnel-arrow"
                      color={DASHBOARD_COLORS.text3}
                      component="span"
                    >
                      →
                    </TypographyCompo>
                  ) : null}
                  <FunnelStep step={step} />
                </Fragment>
              ))}
            </div>
          </SequentialBlock>
        </div>

        <CardShell title="Pending Transfers">
          <SequentialBlock
            loadingState={loadingState}
            section="stockTransfer"
            block="table"
            skeleton={
              <TableSkeleton
                rows={5}
                columns={PENDING_TRANSFERS_COLUMNS.length}
              />
            }
          >
            <SearchableDashboardTable
              columns={PENDING_TRANSFERS_COLUMNS}
              rows={pendingTransfers}
              scrollable
              placeholder="Search transfer no, sales team..."
              getRowKey={(row) => row.id || row.transferNo}
            />
          </SequentialBlock>
        </CardShell>
      </div>
    </section>
  );
}

export function SettlementSection({ settlementKpis, pendingSettlements, loadingState }) {
  return (
    <section className="dash-section" id={SECTION_IDS.SETTLEMENT}>
      <SectionHeader
        icon="🧾"
        iconBackground={SECTION_ICON_BACKGROUNDS.green}
        title="Settlement Dashboard"
      />

      <div className="section-body">
        <SequentialBlock
          loadingState={loadingState}
          section="settlement"
          block="kpis"
          skeleton={<KpiGridSkeleton columns="g3" count={3} />}
        >
          <KpiGrid items={settlementKpis} columns="g3" />
        </SequentialBlock>

        <CardShell title="Pending Settlement List">
          <SequentialBlock
            loadingState={loadingState}
            section="settlement"
            block="table"
            skeleton={
              <TableSkeleton
                rows={5}
                columns={PENDING_SETTLEMENTS_COLUMNS.length}
              />
            }
          >
            <SearchableDashboardTable
              columns={PENDING_SETTLEMENTS_COLUMNS}
              rows={pendingSettlements}
              placeholder="Search salesman, urgency..."
              getRowKey={(row) => row.id || row.salesman}
            />
          </SequentialBlock>
        </CardShell>
      </div>
    </section>
  );
}
